import axios from "axios"
import type { Err, Result } from "true-myth/result"

import type { ErrorInterface } from "@/components/global/Errors/Errors"

export interface ApiError {
  message: string
  status?: number
  code?: string
  raw?: unknown
  cause?: unknown
}

export type ApiResult<T> = Result<T, ApiError>
export type ApiResultPromise<T> = Promise<ApiResult<T>>

/**
 * Validation errors returned by the API as a map of field names to error messages.
 * Used by mutation modals to display server-side validation feedback on form fields.
 */
export type ValidationErrors = Record<string, string[]>

export function isValidationErrors(raw: unknown): raw is ValidationErrors {
  return (
    isRecord(raw) &&
    Object.values(raw).every(
      (value) => Array.isArray(value) && value.every((item) => typeof item === "string"),
    )
  )
}

/**
 * Key used for general (non-field-specific) error messages extracted from
 * `halt_with_error` responses (`{ error: "message" }`).
 */
export const GENERAL_ERROR_KEY = "_error"

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function extractString(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function extractApiErrorPayloadMessage(data: unknown): string | undefined {
  if (!isRecord(data)) {
    return undefined
  }
  const directMessage = extractString(data.message) ?? extractString(data.error)
  if (directMessage !== undefined) {
    return directMessage
  }
  const nestedErrors = isRecord(data.data) ? data.data.errors : undefined
  if (Array.isArray(nestedErrors)) {
    const nestedMessage = nestedErrors.find(
      (item): item is string => typeof item === "string",
    )
    if (nestedMessage) {
      return nestedMessage
    }
  }
  for (const value of Object.values(data)) {
    if (Array.isArray(value)) {
      const fieldMessage = value.find((item): item is string => typeof item === "string")
      if (fieldMessage) {
        return fieldMessage
      }
    }
  }
  return undefined
}

function extractApiErrorPayloadCode(data: unknown): string | undefined {
  if (!isRecord(data)) {
    return undefined
  }
  return extractString(data.code)
}

export function normalizeApiError(error: unknown, fallbackMessage: string): ApiError {
  if (!axios.isAxiosError(error)) {
    return { message: fallbackMessage, cause: error }
  }
  const data = error.response?.data
  const apiError: ApiError = {
    message: extractApiErrorPayloadMessage(data) ?? fallbackMessage,
    cause: error,
  }
  if (error.response?.status !== undefined) {
    apiError.status = error.response.status
  }
  const code = extractApiErrorPayloadCode(data)
  if (code !== undefined) {
    apiError.code = code
  }
  if (data !== undefined) {
    apiError.raw = data
  }
  return apiError
}

export function isApiError(error: unknown): error is ApiError {
  if (typeof error !== "object" || error === null || Array.isArray(error)) {
    return false
  }
  if (error instanceof Error) {
    return false
  }
  const apiError = error as { message?: unknown }
  return typeof apiError.message === "string"
}

/**
 * Extract a human-readable error message from an API response body.
 *
 * Handles the same two response shapes as {@link extractValidationErrors}:
 *
 * 1. `halt_with_error`  — `{ error: "message" }` → returns that message.
 * 2. `halt_with_errors` — `{ field: ["msg", …] }` → returns the first string
 *    found across all field arrays.
 *
 * Returns `undefined` for responses without a parseable message, letting
 * callers fall back to their own default.
 */
export function getApiErrorPayloadMessage(raw: unknown): string | undefined {
  return extractApiErrorPayloadMessage(raw)
}

export function getApiError(
  error: unknown,
  fallbackMessage = "An unexpected error occurred",
): ApiError {
  if (isApiError(error)) {
    return error
  }
  if (error instanceof Error && isApiError(error.cause)) {
    return error.cause
  }
  if (error instanceof Error && axios.isAxiosError(error.cause)) {
    return normalizeApiError(error.cause, fallbackMessage)
  }
  if (axios.isAxiosError(error)) {
    return normalizeApiError(error, fallbackMessage)
  }
  return { message: fallbackMessage, cause: error }
}

/**
 * Extract validation errors from a caught exception.
 *
 * Handles two response shapes from API controllers (both are 422):
 *
 * 1. `halt_with_errors` — `{ field: ["error1", "error2"] }` — field-level
 *    validation errors, returned as-is.
 *
 * 2. `halt_with_error` — `{ error: "message" }` — a single general error
 *    message, normalised to `{ _error: ["message"] }` so callers can display
 *    it in a banner without coupling to a specific form field.
 *
 * `axios-case-converter` rewrites keys to camelCase before they reach this
 * code.
 *
 * Non-Axios errors, non-422 status codes, and malformed bodies are normalised
 * to an empty object so callers can unconditionally pass the result to
 * `setValidationErrors`.
 */
export function extractValidationErrors(error: unknown): ValidationErrors {
  const apiError = getApiError(error, "API error")

  if (apiError.status !== 422) {
    return {}
  }

  const data = apiError.raw
  if (!isRecord(data)) {
    return {}
  }

  // Handle `halt_with_error` shape: { error: "message" }
  if ("error" in data) {
    const msg = data.error
    if (typeof msg === "string") {
      return { [GENERAL_ERROR_KEY]: [msg] }
    }
  }

  // Handle `halt_with_errors` shape: { field: ["error1", "error2"] }
  const result: ValidationErrors = {}
  for (const [key, value] of Object.entries(data)) {
    if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
      result[key] = value
    }
  }
  return result
}

/**
 * Like {@link extractValidationErrors}, but guarantees a non-empty result.
 *
 * For 422 responses with parseable field/general errors, returns those errors
 * unchanged. For every other case (non-422 status, network errors, malformed
 * bodies) falls back to a general error entry so the caller always has
 * something to display — preventing modals from silently swallowing failures.
 */
export function extractValidationErrorsWithFallback(
  error: unknown,
  fallbackMessage = "An unexpected error occurred",
): ValidationErrors {
  const apiError = getApiError(error, fallbackMessage)
  const fieldErrors = extractValidationErrors(apiError)
  const hasRenderableMessage = Object.values(fieldErrors).some((messages) =>
    messages.some((m) => m.trim().length > 0),
  )
  if (hasRenderableMessage) {
    return fieldErrors
  }
  const apiMessage = getApiErrorPayloadMessage(apiError.raw)?.trim()
  return {
    [GENERAL_ERROR_KEY]: [
      apiMessage && apiMessage.length > 0 ? apiMessage : apiError.message,
    ],
  }
}

/**
 * Render a mutation `Result` as form validation errors and narrow the type on
 * success.
 *
 * If `result` is an `Err`, the `ApiError` is passed to
 * `extractValidationErrorsWithFallback` so field-level 422 errors are preserved
 * and a fallback message is always shown. Callers should return immediately
 * when this function returns `true`.
 */
export function applyMutationError<T>(
  result: ApiResult<T>,
  setValidationErrors: (errors: ValidationErrors) => void,
): result is Err<T, ApiError> {
  if (result.isErr) {
    setValidationErrors(
      extractValidationErrorsWithFallback(result.error, result.error.message),
    )
    return true
  }
  return false
}

function isErrorData(value: unknown): value is ErrorInterface["data"] {
  if (typeof value === "string") {
    return true
  }
  return isRecord(value)
}

export function apiErrorToErrorInterface(apiError: ApiError): ErrorInterface {
  const status = apiError.status ?? 500
  const data: ErrorInterface["data"] = isErrorData(apiError.raw)
    ? apiError.raw
    : apiError.message
  return { status, data }
}
